import React, { useState, useEffect } from 'react';
import { safeJson, networkMessage } from '../apiHelpers';
import { t } from '../i18n';
import { SERVICE_CATEGORIES } from '../serviceCategories';

const colors = {
  bg: '#F7F5F0',
  surface: '#fff',
  border: '#E3E1D9',
  text: '#1F1E1B',
  textMuted: '#5F5E5A',
  primary: '#0F6E56',
};

const API_BASE = '/api/services';

function getAuthToken() {
  return localStorage.getItem('token') || '';
}

const inputStyle = {
  width: '100%', padding: '10px 12px', borderRadius: 10, border: `0.5px solid ${colors.border}`,
  fontSize: 13, marginBottom: 10, boxSizing: 'border-box',
};

export default function ServiceProviderRegister() {
  const [form, setForm] = useState({
    category: 'plumber', business_name: '', description: '', city: '', area: '',
    phone: '', whatsapp: '', years_experience: 0, hourly_rate_pi: '',
    latitude: '', longitude: '',
  });
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = getAuthToken();
    if (!token) return;
    fetch(`${API_BASE}/my-profile/`, { headers: { Authorization: `Token ${token}` } })
      .then((res) => res.json().catch(() => ({})))
      .then((data) => { if (data && data.provider) setForm({ ...form, ...data.provider }); })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const useMyLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setForm({ ...form, latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => {},
    );
  };

  const submit = async () => {
    const token = getAuthToken();
    if (!token) { window.location.href = '/'; return; }
    if (!form.business_name || !form.category) {
      setErr(t('fill_required_fields'));
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await fetch(`${API_BASE}/my-profile/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(form),
      });
      const data = await safeJson(res).catch(() => ({}));
      if (res.ok) { setSaved(true); }
      else { setErr(data.error || `${t('could_not_save')} (${res.status})`); }
    } catch (e) {
      setErr(t('could_not_reach_server'));
    }
    setSaving(false);
  };

  if (saved) {
    return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '2rem', textAlign: 'center' }}>
        <p style={{ fontSize: 18 }}>✅ {t('profile_saved')}</p>
        <a href="/services" style={{ color: colors.primary, textDecoration: 'none' }}>{t('back_to_services')}</a>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '1.5rem', background: colors.bg, minHeight: '100vh', fontFamily: 'sans-serif' }}>
      <a href="/services" style={{ fontSize: 12, color: colors.textMuted, textDecoration: 'none' }}>&larr; {t('services')}</a>
      <h1 style={{ fontSize: 20, margin: '10px 0 4px' }}>{t('become_a_service_provider')}</h1>
      <p style={{ fontSize: 12, color: colors.textMuted, margin: '0 0 1rem' }}>{t('provider_register_sub')}</p>

      {err ? <p style={{ color: '#c0392b', fontSize: 12 }}>{err}</p> : null}

      <label style={{ fontSize: 12, color: colors.textMuted }}>{t('category')}</label>
      <select value={form.category} onChange={set('category')} style={inputStyle}>
        {SERVICE_CATEGORIES.map((c) => (
          <option key={c.key} value={c.key}>{c.icon} {c.label}</option>
        ))}
      </select>

      <label style={{ fontSize: 12, color: colors.textMuted }}>{t('business_name')}</label>
      <input value={form.business_name} onChange={set('business_name')} style={inputStyle} placeholder={t('business_name_placeholder')} />

      <label style={{ fontSize: 12, color: colors.textMuted }}>{t('description')}</label>
      <textarea value={form.description} onChange={set('description')} style={{ ...inputStyle, minHeight: 70 }} placeholder={t('description_placeholder')} />

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>{t('city')}</label>
          <input value={form.city} onChange={set('city')} style={inputStyle} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>{t('area')}</label>
          <input value={form.area} onChange={set('area')} style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>{t('phone')}</label>
          <input value={form.phone} onChange={set('phone')} style={inputStyle} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>{t('whatsapp')}</label>
          <input value={form.whatsapp} onChange={set('whatsapp')} style={inputStyle} />
        </div>
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>{t('years_experience')}</label>
          <input type="number" min="0" value={form.years_experience} onChange={set('years_experience')} style={inputStyle} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>{t('hourly_rate_pi')}</label>
          <input type="number" min="0" step="0.01" value={form.hourly_rate_pi} onChange={set('hourly_rate_pi')} style={inputStyle} />
        </div>
      </div>

      <button onClick={useMyLocation} style={{ ...inputStyle, background: colors.surface, cursor: 'pointer', textAlign: 'left' }}>
        📍 {form.latitude ? `${t('location_set')} ✔` : t('use_my_location')}
      </button>

      <button
        onClick={submit}
        disabled={saving}
        style={{ width: '100%', padding: '12px 0', borderRadius: 10, background: colors.primary, color: '#fff', border: 'none', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
      >
        {saving ? t('saving') : t('save_profile')}
      </button>
    </div>
  );
}
